/** Auth screens are never a post-auth destination (avoids login → login loops). */
const AUTH_PATHS = ['/login', '/signup', '/forgot-password', '/logout'];
const MAX_NEXT_LENGTH = 1000;

/**
 * Same-origin in-app path for post-auth redirects.
 * Rejects protocol-relative (`//evil.com`), backslash (`/\evil.com`), control
 * characters, absolute URLs, and auth screens — falls back instead.
 */
export function safeNextPath(raw, fallback = '/') {
  if (typeof raw !== 'string') return fallback;
  const value = raw.trim();
  if (!value || value.length > MAX_NEXT_LENGTH) return fallback;
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  // eslint-disable-next-line no-control-regex -- reject control chars and backslashes outright
  if (/[\u0000-\u001f\u007f\\]/.test(value)) return fallback;
  // The router matches case-insensitively on the decoded path (`/LOGIN`, `/%6Cogin`).
  let pathname = value.split(/[?#]/)[0];
  try {
    pathname = decodeURIComponent(pathname);
  } catch {
    return fallback;
  }
  pathname = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  if (AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return fallback;
  return value;
}

/** Destinations where the visitor intends to pay — skip onboarding detours after auth. */
export function isCheckoutPath(path = '') {
  const value = String(path || '');
  return (
    /^\/(pricing|finish-purchase|billing)(?=$|[/?#])/.test(value) ||
    value.includes('#purchase') ||
    value.includes('#results-checkout')
  );
}

/** Current in-app location to return to after login, or '' when there is nothing to return to. */
export function returnPathFor({ pathname = '/', search = '', hash = '' } = {}) {
  if (pathname === '/') return '';
  return safeNextPath(`${pathname}${search}${hash}`, '');
}

/** `/login` or `/signup` URL that brings the visitor back to `next` afterwards. */
export function authHref(page, { next = '', intent = '', email = '' } = {}) {
  const params = new URLSearchParams();
  const safe = safeNextPath(next, '');
  if (safe && safe !== '/') params.set('next', safe);
  if (email) params.set('email', email);
  if (intent) params.set('intent', intent);
  const query = params.toString();
  return `/${page}${query ? `?${query}` : ''}`;
}
